import { useEffect, useMemo, useState } from 'react'
import { X } from 'lucide-react'
import type { Beat, ChannelProfile, ChannelProfileInput, PresetFormat } from '@/api/client'
import { cn } from '@/lib/utils'
import { Badge, Button, FormRow, Input, Select, Textarea } from '@/admin-design/_common'

interface Props {
  /** Existing profile when editing; undefined for create. */
  initial?: ChannelProfile
  onSubmit: (input: ChannelProfileInput) => void | Promise<void>
  onCancel: () => void
  /** Outer-controlled busy flag (disables buttons during mutation). */
  busy?: boolean
  /** Error line rendered below the form footer. */
  error?: string | null
}

// Lightweight kebab transform (mirrors backend `kebab()`). Used only to
// preview the auto-derived slug; backend recomputes server-side on submit.
function kebab(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
}

export default function ChannelProfileForm({ initial, onSubmit, onCancel, busy, error }: Props) {
  const [name, setName] = useState(initial?.name ?? '')
  const [slug, setSlug] = useState(initial?.slug ?? '')
  const [slugTouched, setSlugTouched] = useState(Boolean(initial?.slug))
  const [audience, setAudience] = useState(initial?.audience ?? '')
  const [voice, setVoice] = useState<string[]>(initial?.voice ?? [])
  const [taboo, setTaboo] = useState<string[]>(initial?.taboo_words ?? [])
  const [samples, setSamples] = useState<string[]>(
    initial?.sample_scripts?.length ? initial.sample_scripts : [''],
  )
  const [defaultCTA, setDefaultCTA] = useState(initial?.default_cta ?? '')
  const [bgm, setBGM] = useState(initial?.bgm_preference ?? '')
  const [thumb, setThumb] = useState(initial?.thumbnail_style ?? '')
  // Preset fields (migration 0028).
  const [format, setFormat] = useState<PresetFormat>(initial?.format ?? 'short')
  const [structure, setStructure] = useState<Beat[]>(initial?.structure ?? [])
  const [titleRule, setTitleRule] = useState(initial?.title_rule ?? '')
  const [mandatoryTags, setMandatoryTags] = useState<string[]>(initial?.mandatory_tags ?? [])
  const [tagStyle, setTagStyle] = useState(initial?.tag_style ?? '')
  const [directingStyle, setDirectingStyle] = useState(initial?.directing_style ?? '')
  const [referenceGuide, setReferenceGuide] = useState(initial?.reference_guide ?? '')

  // Auto-suggest slug from name while user hasn't manually edited it.
  useEffect(() => {
    if (!slugTouched) setSlug(kebab(name))
  }, [name, slugTouched])

  const voiceOk = voice.length >= 3 && voice.length <= 7
  const samplesCleaned = useMemo(() => samples.map((s) => s.trim()).filter(Boolean), [samples])
  const samplesOk = samplesCleaned.length <= 3
  const nameOk = name.trim().length > 0
  const canSubmit = nameOk && voiceOk && samplesOk && !busy

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    const payload: ChannelProfileInput = {
      slug: slug.trim(),
      name: name.trim(),
      audience: audience.trim(),
      voice: voice.map((v) => v.trim()).filter(Boolean),
      taboo_words: taboo.map((v) => v.trim()).filter(Boolean),
      sample_scripts: samplesCleaned,
      default_cta: defaultCTA.trim(),
      bgm_preference: bgm.trim(),
      thumbnail_style: thumb.trim(),
      format,
      structure: structure
        .map((b) => ({ label: b.label.trim(), desc: b.desc.trim() }))
        .filter((b) => b.label !== ''),
      title_rule: titleRule.trim(),
      mandatory_tags: mandatoryTags.map((t) => t.trim()).filter(Boolean),
      tag_style: tagStyle.trim(),
      directing_style: directingStyle.trim(),
      reference_guide: referenceGuide.trim(),
    }
    void onSubmit(payload)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormRow label="Name" required>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="예: 60초 상식 쇼츠"
          required
        />
      </FormRow>

      <FormRow label="Slug" help="auto-kebab from name">
        <Input
          className="font-mono"
          value={slug}
          onChange={(e) => {
            setSlugTouched(true)
            setSlug(e.target.value)
          }}
          placeholder="my-channel"
        />
      </FormRow>

      <FormRow label="Audience">
        <Textarea
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
          rows={2}
          placeholder="20대 여성, 자기계발에 관심있는"
        />
      </FormRow>

      <TagInput
        label="Voice (3-7 adjectives)"
        required
        tags={voice}
        setTags={setVoice}
        placeholder="witty, fast-paced, curious..."
        validClass={cn(voiceOk ? 'text-muted-foreground' : 'text-destructive', 'text-[11px] mt-1')}
        validMessage={`${voice.length}/7 · ${voiceOk ? 'OK' : '3-7 adjectives required'}`}
      />

      <TagInput
        label="Taboo words"
        tags={taboo}
        setTags={setTaboo}
        placeholder="avoid these words"
      />

      <SampleScriptsInput samples={samples} setSamples={setSamples} />

      <FormRow label="Default CTA">
        <Input
          value={defaultCTA}
          onChange={(e) => setDefaultCTA(e.target.value)}
          placeholder="구독과 좋아요 부탁해요!"
        />
      </FormRow>

      <FormRow label="BGM preference">
        <Input value={bgm} onChange={(e) => setBGM(e.target.value)} placeholder="upbeat lo-fi" />
      </FormRow>

      <FormRow label="Thumbnail style">
        <Input
          value={thumb}
          onChange={(e) => setThumb(e.target.value)}
          placeholder="큰 얼굴 + 붉은 문구"
        />
      </FormRow>

      <div className="pt-3 mt-1 border-t border-border">
        <p className="text-xs font-semibold text-muted-foreground mb-3">프리셋 (연출·메타)</p>

        <FormRow label="Format">
          <Select
            value={format}
            onValueChange={(v) => setFormat(v as PresetFormat)}
            options={[
              { label: 'short (숏폼 ~60초)', value: 'short' },
              { label: 'long (롱폼 ~20분)', value: 'long' },
              { label: 'both (둘 다)', value: 'both' },
            ]}
          />
        </FormRow>

        <StructureInput beats={structure} setBeats={setStructure} />

        <FormRow label="Title rule" help="제목 후킹 규칙">
          <Textarea
            value={titleRule}
            onChange={(e) => setTitleRule(e.target.value)}
            rows={2}
            placeholder="미완성 문장·의문형. [관계]+[의외의 사건] 조합"
          />
        </FormRow>

        <TagInput
          label="Mandatory tags"
          tags={mandatoryTags}
          setTags={setMandatoryTags}
          placeholder="항상 포함할 태그"
        />

        <FormRow label="Tag style" help="자동 태그 조합 힌트">
          <Input
            value={tagStyle}
            onChange={(e) => setTagStyle(e.target.value)}
            placeholder="필수 태그 + 소재 연관 태그 2~4개"
          />
        </FormRow>

        <FormRow label="Directing style" help="storygen 연출 톤">
          <Textarea
            value={directingStyle}
            onChange={(e) => setDirectingStyle(e.target.value)}
            rows={2}
            placeholder="1인칭 회상 톤, 잔잔한 정지컷·느린 줌"
          />
        </FormRow>

        <FormRow
          label="Reference guide (RAG)"
          help="장르 플레이북 — 근거·모티프로만, 원작 복제 금지"
        >
          <Textarea
            value={referenceGuide}
            onChange={(e) => setReferenceGuide(e.target.value)}
            rows={5}
            placeholder="캐릭터 원형 / 서사구조 / 훅 공식 / 반전 카탈로그…"
          />
        </FormRow>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2 pt-2 border-t border-border">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={busy}>
          취소
        </Button>
        <Button type="submit" variant="primary" size="sm" disabled={!canSubmit} loading={busy}>
          {initial ? '저장' : '추가'}
        </Button>
      </div>
    </form>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// TagInput — pill-style multi-value entry. Enter / comma commits a tag.
// ─────────────────────────────────────────────────────────────────────────

interface TagInputProps {
  label: string
  tags: string[]
  setTags: (next: string[]) => void
  placeholder?: string
  required?: boolean
  validClass?: string
  validMessage?: string
}

function TagInput({
  label,
  tags,
  setTags,
  placeholder,
  required,
  validClass,
  validMessage,
}: TagInputProps) {
  const [draft, setDraft] = useState('')

  const commit = () => {
    const v = draft.trim()
    if (!v) return
    if (!tags.includes(v)) setTags([...tags, v])
    setDraft('')
  }

  const remove = (idx: number) => setTags(tags.filter((_, i) => i !== idx))

  return (
    <div>
      <label className="block text-xs font-medium text-foreground mb-1">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      <div className="border border-border rounded-md px-2 py-1.5 flex flex-wrap gap-1.5 min-h-[40px] bg-card shadow-sm">
        {tags.map((t, i) => (
          <Badge
            key={`${t}-${i}`}
            className="rounded inline-flex items-center gap-1 px-2 py-0.5 text-xs"
          >
            {t}
            <button
              type="button"
              onClick={() => remove(i)}
              className="hover:text-destructive"
              aria-label={`remove ${t}`}
            >
              <X size={12} />
            </button>
          </Badge>
        ))}
        <input
          className="flex-1 min-w-[120px] outline-none text-sm px-1 bg-transparent text-foreground"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault()
              commit()
            } else if (e.key === 'Backspace' && !draft && tags.length > 0) {
              remove(tags.length - 1)
            }
          }}
          onBlur={commit}
          placeholder={tags.length === 0 ? placeholder : ''}
        />
      </div>
      {validMessage && <p className={validClass}>{validMessage}</p>}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// SampleScriptsInput — up to 3 textareas for inspiration scripts.
// ─────────────────────────────────────────────────────────────────────────

interface SampleScriptsInputProps {
  samples: string[]
  setSamples: (next: string[]) => void
}

function SampleScriptsInput({ samples, setSamples }: SampleScriptsInputProps) {
  const update = (idx: number, value: string) => {
    const next = [...samples]
    next[idx] = value
    setSamples(next)
  }
  const remove = (idx: number) => {
    const next = samples.filter((_, i) => i !== idx)
    setSamples(next.length === 0 ? [''] : next)
  }
  const add = () => {
    if (samples.length >= 3) return
    setSamples([...samples, ''])
  }

  return (
    <div>
      <label className="block text-xs font-medium text-foreground mb-1">
        Sample scripts <span className="text-muted-foreground">(up to 3)</span>
      </label>
      <div className="space-y-2">
        {samples.map((s, i) => (
          <div key={i} className="flex gap-2 items-start">
            <Textarea
              block={false}
              className="flex-1"
              value={s}
              onChange={(e) => update(i, e.target.value)}
              rows={2}
              placeholder={`Sample ${i + 1}`}
            />
            {samples.length > 1 && (
              <button
                type="button"
                onClick={() => remove(i)}
                className="p-1 text-muted-foreground hover:text-destructive"
                aria-label="remove sample"
              >
                <X size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
      {samples.length < 3 && (
        <Button
          type="button"
          variant="link"
          size="sm"
          onClick={add}
          className="mt-1 h-auto p-0 text-xs"
        >
          + Add another sample
        </Button>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// StructureInput — ordered {label, desc} beat rows (genre structure template).
// ─────────────────────────────────────────────────────────────────────────

interface StructureInputProps {
  beats: Beat[]
  setBeats: (next: Beat[]) => void
}

function StructureInput({ beats, setBeats }: StructureInputProps) {
  const update = (idx: number, patch: Partial<Beat>) => {
    const next = beats.map((b, i) => (i === idx ? { ...b, ...patch } : b))
    setBeats(next)
  }
  const remove = (idx: number) => setBeats(beats.filter((_, i) => i !== idx))
  const add = () => setBeats([...beats, { label: '', desc: '' }])

  return (
    <div className="mb-4">
      <label className="block text-xs font-medium text-foreground mb-1">
        Structure <span className="text-muted-foreground">(비트 순서: 라벨 + 설명)</span>
      </label>
      <div className="space-y-2">
        {beats.map((b, i) => (
          <div key={i} className="flex min-w-0 items-start gap-2">
            <div className="w-28 shrink-0 sm:w-32">
              <Input
                value={b.label}
                onChange={(e) => update(i, { label: e.target.value })}
                placeholder={`비트 ${i + 1}`}
              />
            </div>
            <div className="min-w-0 flex-1">
              <Input
                value={b.desc}
                onChange={(e) => update(i, { desc: e.target.value })}
                placeholder="설명"
              />
            </div>
            <button
              type="button"
              onClick={() => remove(i)}
              className="mt-1.5 shrink-0 p-1 text-muted-foreground hover:text-destructive"
              aria-label="remove beat"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="link"
        size="sm"
        onClick={add}
        className="mt-1 h-auto p-0 text-xs"
      >
        + Add beat
      </Button>
    </div>
  )
}
